import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  // Pop plays first, then switch to pulse
  useEffect(() => {
    const timer = setTimeout(() => setPulsing(true), 1600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:rounded" data-testid="skip-to-content">Skip to content</a>
      <div className="fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
        {/* Calculator banner — pop on load, then pulse */}
        <a href="https://calculator.compareworkerscomp.com" className={`${pulsing ? 'banner-do-pulse' : 'banner-pop-pulse'} block bg-cwc-navy border-b border-white/10 py-2 px-4 hover:bg-cwc-navy-light transition-colors`} data-testid="calculator-banner-nav">
          <div className="banner-inner max-w-4xl mx-auto flex items-center justify-center gap-2 md:gap-3">
            <span className="text-white text-[11px] md:text-sm font-medium"><span className="hidden sm:inline">See what workers comp should cost you — </span><strong className="text-yellow-400">Free 60-Second Calculator</strong></span>
            <span className="text-white/50 text-[10px] hidden md:inline">No calls. No email needed.</span>
            <span className="banner-cta-ping bg-red-600 text-white text-[10px] md:text-[11px] font-bold px-2.5 md:px-3 py-1 rounded-full whitespace-nowrap">Check My Rate &rarr;</span>
          </div>
        </a>
        {/* Nav bar */}
        <nav role="navigation" aria-label="Main navigation" className={`transition-all duration-300 ${isHome && !scrolled ? 'bg-cwc-navy/80 backdrop-blur-md' : 'bg-cwc-navy shadow-lg'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/trio-logo.png" alt="Trio Insurance - CompareWorkersComp.com" className="h-10 lg:h-12" />
            </Link>
            {/* Desktop nav — only shows at lg (1024px+) */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-7">
              <Link to="/cities" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors whitespace-nowrap" data-testid="nav-cities">Find Your City</Link>
              <Link to="/workers-comp-for-construction-companies" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors" data-testid="nav-construction">Construction</Link>
              <Link to="/workers-comp-for-restaurants-companies" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors" data-testid="nav-restaurants">Restaurants</Link>
              <a href="https://www.dentalworkerscomp.com/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors" data-testid="nav-dental">Dental</a>
              <a href="/#industry-cards" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors whitespace-nowrap" data-testid="nav-more">And More</a>
              <Link to="/about" className="text-white/80 hover:text-white text-sm xl:text-base font-medium transition-colors" data-testid="nav-about">About</Link>
              <a href="/#lead-form" className="bg-red-600 text-white font-bold px-5 xl:px-6 py-2.5 rounded-full text-sm xl:text-base hover:bg-red-700 transition-colors whitespace-nowrap" data-testid="nav-cta">Get Free Quotes</a>
            </div>
            {/* Hamburger — shows below lg */}
            <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
              {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
        {/* Mobile/tablet menu */}
        {open && (
          <div className="lg:hidden bg-cwc-navy border-t border-white/10 px-5 py-5 space-y-1" data-testid="nav-mobile-menu">
            <Link to="/cities" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">Find Your City</Link>
            <Link to="/workers-comp-for-construction-companies" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">Construction</Link>
            <Link to="/workers-comp-for-restaurants-companies" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">Restaurants</Link>
            <a href="https://www.dentalworkerscomp.com/" target="_blank" rel="noopener noreferrer" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">Dental</a>
            <a href="/#industry-cards" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">And More</a>
            <Link to="/about" className="block text-white/80 hover:text-white hover:bg-white/5 py-3 px-3 rounded-lg text-base font-medium transition-colors">About</Link>
            <div className="pt-2">
              <a href="/#lead-form" className="block bg-red-600 text-white font-bold py-3 rounded-full text-base text-center hover:bg-red-700 transition-colors">Get Free Quotes</a>
            </div>
          </div>
        )}
      </nav>
      </div>
    </>
  );
}
